import type { ReactNode } from 'react';

import { KioskCard } from '@/components/KioskCard';

export interface EnrolmentShellProps {
  readonly children: ReactNode;
}

/**
 * The page every device-enrolment state is drawn on [Figma · SETUP — Device enrolment (PIN)].
 *
 * **It is not `KioskScreen`, and the differences are the design's rather than a preference.** Every frame in
 * this section is missing all three of that shell's chrome pieces, verified against the node tree of all
 * eighteen frames this story owns:
 *
 *  - ⛔ **No `Need Help FAB`.** Absent from every frame of the section — correct, not an omission: these
 *    screens are staff-facing, and KVE-1's *talk to a recruiter* affordance on a screen only staff see would
 *    offer a recruiter to a recruiter.
 *  - ⛔ **No language toggle.** The enrolment flow is English-only [decision 0226]; a toggle here would offer
 *    a Spanish that does not exist.
 *  - ⛔ **No Exit control.** There is no candidate session to end — nobody has checked in yet.
 *
 * The card itself is shared: 768 at desktop, 553 at tablet, full-bleed at 375, the same logo block and the
 * same 448 content column, which is exactly what `KioskCard` already measures.
 */
export function EnrolmentShell({ children }: EnrolmentShellProps) {
  return (
    /*
     * `pt-0` below `sm` and 24 from `sm` up, the same responsive rule `KioskScreen` carries and read off the
     * same frames: `01 · PIN · Enter — Mobile` (12021:41259) opens its logo block flush at y=0, while the
     * iPad and desktop frames inset the card by 24. There is no language bar above the card here, so the
     * page's padding IS the card's offset.
     */
    <main className="flex min-h-screen flex-col items-center bg-[var(--surface)] pb-8 pt-0 font-sans sm:px-4 sm:pt-6">
      {/*
        `bareInset={false}`: these cards put their logo block flush against the card's top edge at every
        width — card at y=24, `Logo` at y=24 on `01 · PIN · Enter — Desktop` (12021:41617) — where Welcome
        and Have-you-applied inset theirs by 48 at 1440. Same shape as `10 · Submitted`, which is the other
        card in the app with nothing above its content.
      */}
      <KioskCard bareInset={false}>{children}</KioskCard>
    </main>
  );
}
